/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from 'react';
import AceEditor from 'react-ace';

import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-tomorrow';

import { Button, Box, Typography, Divider, Link, Paper } from '@mui/material';
import PropTypes from 'prop-types';
import PublishIcon from '@mui/icons-material/Publish';
import DownloadIcon from '@mui/icons-material/Download';
import ValidIcon from '@mui/icons-material/CheckCircleSharp';
import InvalidIcon from '@mui/icons-material/CancelSharp';

import Session from '../../services/session';

import localization from '../../localization';
import './jsonEditor.scss';

const JsonEditor = ({
  currentData,
  setCurrentData,
  title = 'JSON',
  jsonKey,
  height,
  onSave,
  cursorPage,
  isReadOnly,
  showUploadButton,
  isCustomSample,
  isItFromCodeMode,
}) => {
  const editorRef = useRef(null);
  const [jsonIsValid, setJsonIsValid] = useState(true);
  const [isRendering, setRendering] = useState(true);
  const [editorData, setEditorData] = useState('');

  const jsonData = jsonKey ? currentData[jsonKey] : editorData;

  const downloadJsonData = `data:text/json;charset=utf-8,${encodeURIComponent(jsonData)}`;
  const downloadName = `${currentData?.name || 'data'}.json`;

  const handleCursorChange = (selection) => {
    const cursorPosition = selection.getCursor();

    if (!isRendering && cursorPage && cursorPosition) {
      Session.setEditorCursor(cursorPage, `${cursorPosition.row + 1}:${cursorPosition.column}`);
    }
  };

  const handleChange = (newValue) => {
    if (isCustomSample) {
      setEditorData(newValue);
    }

    if (jsonKey) {
      setCurrentData && setCurrentData({ ...currentData, [jsonKey]: newValue });
      setEditorData(newValue);
    } else {
      setCurrentData && setCurrentData(newValue);
      isItFromCodeMode && setEditorData(newValue)
    }
  };

  useEffect(() => {
    try {
      const newValue = jsonKey ? currentData[jsonKey] : currentData;
      JSON.parse(newValue);
      setJsonIsValid(true);
    } catch {
      setJsonIsValid(false);
    }
  }, [currentData, jsonKey]);

  useEffect(() => {
    setEditorData(currentData);
  }, [currentData]);

  const handleJsonUpload = (e) => {
    e.persist();

    const reader = new FileReader();
    reader.readAsText(e.target.files[0]);

    reader.onload = () => {
      setEditorData(reader.result);

      if (jsonKey) {
        setCurrentData && setCurrentData({
          ...currentData,
          [jsonKey]: reader.result,
        });
      } else {
        setCurrentData && setCurrentData(reader.result);
      }
    };
  };

  useEffect(() => {
    if (editorRef?.current?.editor) {
      let rendering = true;

      editorRef.current.editor.renderer.on('afterRender', () => {
        if (rendering) {
          const positionSettings = cursorPage && Session.getEditorCursor(cursorPage);

          if (positionSettings) {
            const [row, column] = positionSettings.split(':');
            editorRef.current.editor.gotoLine(row, column);
          }

          setRendering(false);

          rendering = false;
        }
      });
    }
  }, [editorRef]);

  return (
    <Box
      boxShadow={2}
      p={2}
      component={Paper}
      height={1}
      display="flex"
      flexDirection="column"
    >
      <Box width={1} display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <Typography variant="h4">
            {title}
              &nbsp;
          </Typography>
          {!isReadOnly && (
          <>
            <Typography>
              {localization.t('labels.validation')}
            </Typography>
            <Box position="relative" top="8px" ml={1}>
              {jsonIsValid
                ? <ValidIcon fontSize="large" color="success" />
                : <InvalidIcon fontSize="large" color="error" />}
            </Box>
          </>
          )}
        </Box>
        {!isReadOnly && !showUploadButton &&
            (
              <Box display="flex">
                {onSave && (
                  <Button
                    disabled={!jsonIsValid || currentData === editorData}
                    onClick={() => onSave(editorData)}
                    variant="outlined"
                    style={{ marginRight: '10px' }}
                  >
                    SAVE
                  </Button>
                )}
                <Link
                  underline="none"
                  href={downloadJsonData}
                  download={downloadName}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    style={{ marginRight: '10px' }}
                  >
                    {localization.t('general.download')}
                    <DownloadIcon style={{ marginLeft: 5, fontSize: 20 }} />
                  </Button>
                </Link>

                <Button
                  variant="outlined"
                  component="label"
                >
                  {localization.t('general.upload')}
                  <PublishIcon style={{ marginLeft: 5, fontSize: 20 }} />
                  <input
                    type="file"
                    accept="application/JSON"
                    onChange={handleJsonUpload}
                    hidden
                  />
                </Button>
              </Box>
            )}
      </Box>
      <Divider light />
      <Box flexGrow={1}>
        <AceEditor
          name="jsonEditor"
          theme="tomorrow"
          value={jsonData}
          mode="json"
          readOnly={isReadOnly}
          onChange={handleChange}
          height={height}
          setOptions={{
            showInvisibles: true,
            showPrintMargin: false,
          }}
          ref={editorRef}
          onCursorChange={handleCursorChange}
          width="100%"
          highlightActiveLine
        />
      </Box>
    </Box>
  );
};

JsonEditor.propTypes = {
  currentData: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  setCurrentData: PropTypes.func,
  title: PropTypes.string,
  jsonKey: PropTypes.string,
  height: PropTypes.string,
  isReadOnly: PropTypes.bool,
  showUploadButton: PropTypes.bool,
  onSave: PropTypes.func,
  cursorPage: PropTypes.string,
  isCustomSample: PropTypes.bool,
  isItFromCodeMode: PropTypes.bool,
};

JsonEditor.defaultProps = {
  height: '500px',
};

export default JsonEditor;
